import type {
  AdjustBalanceResponse,
  BillingAnomalyResolvedFilter,
  FailedDeductionResolvedFilter,
  GetAccountDetailResponse,
  ListBillingAnomaliesQuery,
  ListBillingAnomaliesResponse,
  ListAccountsQuery,
  ListAccountsResponse,
  ListAdminApiKeysResponse,
  ListFailedDeductionsQuery,
  ListFailedDeductionsResponse,
  ResolveFailedDeductionResponse,
  RevokeAdminApiKeyResponse,
  MarkEmailVerifiedResponse,
  ResendEmailVerificationResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  type AdjustBalanceClientRequest,
  type MarkEmailVerifiedClientRequest,
  type ResolveFailedDeductionClientRequest,
  type ResendEmailVerificationClientRequest,
  type RevokeAdminApiKeyClientRequest,
  type SoftDeleteAccountResult,
  type SuspendAccountClientRequest,
  type SuspendAccountResult,
  type UnsuspendAccountResult,
  adjustBalance,
  getAccount,
  markEmailVerified,
  listBillingAnomalies,
  listAccounts,
  listAdminApiKeys,
  listFailedDeductions,
  resolveFailedDeduction,
  resendEmailVerification,
  revokeAdminApiKey,
  softDeleteAccount,
  suspendAccount,
  unsuspendAccount,
} from './admin.ts';

/**
 * React Query wrappers for the `/v1/ops/admin/*` proxy.
 *
 * Query key conventions:
 *   - `['admin', 'accounts', query]`                 — account list scoped by
 *     the current search / pagination / filter tuple.
 *   - `['admin', 'accounts', accountId]`             — single-account detail.
 *   - `['admin', 'failed-deductions', filter]`       — failed-deduction list
 *     scoped by the `resolvedFilter` (unresolved / resolved / all).
 *
 * Mutation invalidation map:
 *   - useAdjustBalance         → invalidate the root `['admin','accounts']`
 *     subtree (list + all scoped account details) so the adjusted row's
 *     balance + recent transactions refresh on navigation.
 *   - useResolveFailedDeduction → invalidate the root
 *     `['admin','failed-deductions']` subtree (covers every filter tab —
 *     a row that moves from unresolved → resolved must disappear from the
 *     default tab and show up under the resolved / all tabs).
 */

export const ADMIN_NAMESPACE = ['admin'] as const;
export const ACCOUNTS_ROOT_KEY = ['admin', 'accounts'] as const;
export const FAILED_DEDUCTIONS_ROOT_KEY = ['admin', 'failed-deductions'] as const;
export const BILLING_ANOMALIES_ROOT_KEY = ['admin', 'billing-anomalies'] as const;

export interface AccountsListScope {
  readonly limit: number;
  readonly offset: number;
  readonly email: string;
  readonly hasBalance: boolean;
}

export function accountsListQueryKey(
  scope: AccountsListScope,
): readonly ['admin', 'accounts', AccountsListScope] {
  return ['admin', 'accounts', scope] as const;
}

export function accountQueryKey(
  accountId: string,
): readonly ['admin', 'accounts', string] {
  return ['admin', 'accounts', accountId] as const;
}

export function failedDeductionsQueryKey(
  filter: FailedDeductionResolvedFilter,
): readonly ['admin', 'failed-deductions', FailedDeductionResolvedFilter] {
  return ['admin', 'failed-deductions', filter] as const;
}

export function billingAnomaliesQueryKey(
  filter: BillingAnomalyResolvedFilter,
): readonly ['admin', 'billing-anomalies', BillingAnomalyResolvedFilter] {
  return ['admin', 'billing-anomalies', filter] as const;
}

export function useAccountsList(
  scope: AccountsListScope,
): UseQueryResult<ListAccountsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: accountsListQueryKey(scope),
    queryFn: ({ signal }) => {
      const query: ListAccountsQuery = {
        limit: scope.limit,
        offset: scope.offset,
      };
      const trimmedEmail = scope.email.trim();
      if (trimmedEmail.length > 0) {
        query.email = trimmedEmail;
      }
      if (scope.hasBalance) {
        query.hasBalance = 'true';
      }
      return listAccounts(query, fetchImpl, signal);
    },
  });
}

export function useAccount(
  accountId: string | undefined,
): UseQueryResult<GetAccountDetailResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: accountQueryKey(accountId ?? ''),
    queryFn: ({ signal }) => {
      if (accountId === undefined || accountId.length === 0) {
        throw new Error('useAccount: accountId is required');
      }
      return getAccount(accountId, fetchImpl, signal);
    },
    enabled: accountId !== undefined && accountId.length > 0,
  });
}

export interface AdjustBalanceVars {
  readonly accountId: string;
  readonly body: AdjustBalanceClientRequest;
}

export function useAdjustBalance(): UseMutationResult<
  AdjustBalanceResponse,
  Error,
  AdjustBalanceVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }) => adjustBalance(accountId, body, fetchImpl),
    onSuccess: () => {
      // Invalidating the root `['admin','accounts']` key hits every scoped
      // list variant (different search / pagination tuples) AND every
      // single-account detail in one shot. Simpler and safer than trying
      // to rebuild the exact scope+id keys from the mutation variables.
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
    },
  });
}

export interface ResendEmailVerificationVars {
  readonly accountId: string;
  readonly body: ResendEmailVerificationClientRequest;
}

export function useResendEmailVerification(): UseMutationResult<
  ResendEmailVerificationResponse,
  Error,
  ResendEmailVerificationVars
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: ({ accountId, body }) => resendEmailVerification(accountId, body, fetchImpl),
  });
}

export interface MarkEmailVerifiedVars {
  readonly accountId: string;
  readonly body: MarkEmailVerifiedClientRequest;
}

export function useMarkEmailVerified(): UseMutationResult<
  MarkEmailVerifiedResponse,
  Error,
  MarkEmailVerifiedVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }) => markEmailVerified(accountId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
    },
  });
}

export interface ResolveFailedDeductionVars {
  readonly id: string;
  readonly body: ResolveFailedDeductionClientRequest;
}

export function useResolveFailedDeduction(): UseMutationResult<
  ResolveFailedDeductionResponse,
  Error,
  ResolveFailedDeductionVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => resolveFailedDeduction(id, body, fetchImpl),
    onSuccess: () => {
      // Same reasoning as useAdjustBalance — root-key invalidation catches
      // every `[..., filter]` subtree so a row moving from unresolved →
      // resolved shows correctly on any active tab.
      void qc.invalidateQueries({ queryKey: FAILED_DEDUCTIONS_ROOT_KEY });
    },
  });
}

export function useFailedDeductionsList(
  filter: FailedDeductionResolvedFilter,
  options: { readonly limit?: number; readonly offset?: number } = {},
): UseQueryResult<ListFailedDeductionsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: failedDeductionsQueryKey(filter),
    queryFn: ({ signal }) => {
      const query: ListFailedDeductionsQuery = {
        resolvedFilter: filter,
      };
      if (options.limit !== undefined) {
        query.limit = options.limit;
      }
      if (options.offset !== undefined) {
        query.offset = options.offset;
      }
      return listFailedDeductions(query, fetchImpl, signal);
    },
  });
}

export function useBillingAnomaliesList(
  filter: BillingAnomalyResolvedFilter,
  options: { readonly limit?: number; readonly offset?: number } = {},
): UseQueryResult<ListBillingAnomaliesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: billingAnomaliesQueryKey(filter),
    queryFn: ({ signal }) => {
      const query: ListBillingAnomaliesQuery = {
        resolvedFilter: filter,
      };
      if (options.limit !== undefined) {
        query.limit = options.limit;
      }
      if (options.offset !== undefined) {
        query.offset = options.offset;
      }
      return listBillingAnomalies(query, fetchImpl, signal);
    },
  });
}

// ─── Tenant API keys: list + revoke (Epic #109 / P0.2) ────────────────
//
// Key scope: orgs. The hook is `enabled: orgId !== undefined` so the
// AccountDetail page can mount it unconditionally — accounts without an
// org (pre-migration-0009 rows, or the seeded acc_system) just render
// an empty list without firing the network call.

const ADMIN_API_KEYS_ROOT_KEY = ['admin', 'api-keys'] as const;

function apiKeysQueryKey(orgId: string): readonly ['admin', 'api-keys', string] {
  return ['admin', 'api-keys', orgId] as const;
}

export function useAdminApiKeys(
  orgId: string | undefined,
): UseQueryResult<ListAdminApiKeysResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: apiKeysQueryKey(orgId ?? ''),
    queryFn: ({ signal }) => {
      if (orgId === undefined || orgId.length === 0) {
        throw new Error('useAdminApiKeys: orgId is required');
      }
      return listAdminApiKeys(orgId, fetchImpl, signal);
    },
    enabled: orgId !== undefined && orgId.length > 0,
  });
}

interface RevokeApiKeyVars {
  readonly id: string;
  readonly body: RevokeAdminApiKeyClientRequest;
}

export function useRevokeAdminApiKey(): UseMutationResult<
  RevokeAdminApiKeyResponse,
  Error,
  RevokeApiKeyVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => revokeAdminApiKey(id, body, fetchImpl),
    onSuccess: () => {
      // Invalidate the whole api-keys subtree — we don't know which
      // org the revoked key belongs to from the mutation input alone,
      // and there's only ever one active api-keys query per mounted
      // detail page, so broad invalidation is cheap.
      void qc.invalidateQueries({ queryKey: ADMIN_API_KEYS_ROOT_KEY });
    },
  });
}

// ─── Account lifecycle (Epic #109 / P2.10) ───────────────────────────────

export interface SuspendAccountVars {
  readonly accountId: string;
  readonly body: SuspendAccountClientRequest;
}

export function useSuspendAccount(): UseMutationResult<
  SuspendAccountResult,
  Error,
  SuspendAccountVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }) => suspendAccount(accountId, body, fetchImpl),
    onSuccess: () => {
      // Same broad-invalidation as useAdjustBalance — the account detail
      // card re-reads from the backend, which now reflects the fresh
      // suspended_at timestamp + disables the Suspend button.
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
    },
  });
}

export function useUnsuspendAccount(): UseMutationResult<
  UnsuspendAccountResult,
  Error,
  { readonly accountId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }) => unsuspendAccount(accountId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
    },
  });
}

export function useSoftDeleteAccount(): UseMutationResult<
  SoftDeleteAccountResult,
  Error,
  { readonly accountId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }) => softDeleteAccount(accountId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
    },
  });
}
